import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useComisionLedger } from '../../hooks/useComisionLedger';
import { useContracts } from '../../contexts/DataContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import TablePager from '../../components/TablePager';
import { FianzaView } from '../../components/FianzaView';
import { ExclamationTriangleIcon, ChevronRightIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { normalizeName } from '../../utils/convenioCompliance';
import { formatNumeroFianza } from '../../utils/calculations';
import { ConvenioPicker, EmptyScope, StatTile, TODOS, fmtMXN, useConvenioScope } from './convenioScope';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const perPage = 10;
const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const MONTHS_LARGOS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const GROUPS = [
  { id: 'convenio', label: 'Convenio' },
  { id: 'afianzadora', label: 'Afianzadora' },
  { id: 'proveedor', label: 'Proveedor' },
];

const TIPO_LABELS = {
  emision: 'Emisión',
  adicional: 'Adicional',
  cargo: 'Cargo',
  sustituye: 'Sustitución',
  renovacion: 'Renovación',
};

const ESTADO_COBRO_LABELS = {
  cobrada: { label: 'Cobrada', className: 'bg-green-100 text-green-800' },
  pendiente: { label: 'Por cobrar', className: 'bg-amber-100 text-amber-800' },
  adeudo: { label: 'Adeudo', className: 'bg-red-100 text-red-800' },
  neutralizada: { label: 'Neutralizada', className: 'bg-gray-100 text-gray-700' },
  adeudo_liquidado: { label: 'Adeudo liquidado', className: 'bg-gray-100 text-gray-700' },
};

const fmtFecha = (ms) =>
  ms
    ? new Date(Number(ms)).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
    : '-';

const blankGroup = (key, label) => ({
  key,
  label,
  bruta: 0,
  participacion: 0,
  neta: 0,
  cargos: 0,
  cobrada: 0,
  porCobrar: 0,
  adeudo: 0,
  movimientos: 0,
  sinPct: 0,
  fianzas: new Map(),
});

const blankFianza = (m) => ({
  fianzaId: m.fianzaId,
  contratoId: m.contratoId,
  numeroFianza: m.numeroFianza,
  afianzadora: m.afianzadora,
  fiadoNombre: m.fiadoNombre,
  convenioNombre: m.convenioNombre,
  tipoFianza: m.tipoFianza,
  fecha: 0,
  prima: 0,
  bruta: 0,
  neta: 0,
  movimientos: 0,
  tipos: new Set(),
  estados: new Set(),
});

/**
 * Producción y comisión por convenio — the money side of the convenios, read
 * off the comisión ledger (movements carry convenioId, so this needs no
 * on-the-fly recalculation).
 *
 * Three figures, kept apart because they answer different questions:
 *   bruta         — the full agent comisión the afianzadora paid on the fianza
 *   participación — the share that belongs to the OTHER agent in the convenio
 *   neta          — what the org actually books (bruta − participación, minus
 *                   cargos from anulaciones)
 * `comisionMonto` is already the net share, and `comisionBase` the gross, so
 * bruta falls back to comisionMonto whenever a movement carries no split.
 *
 * Every row expands into the fianzas behind it — a fianza can carry several
 * movements (emisión + endosos + cargos), so the detail sums them per fianza
 * rather than listing raw rows nobody can reconcile.
 */
export function ComisionPorConvenio({ selectedYear }) {
  const { movements, loading } = useComisionLedger();
  const { contractsById } = useContracts();
  const [convenioId, setConvenioId] = useState(TODOS);
  const [groupBy, setGroupBy] = useState('convenio');
  const [mes, setMes] = useState(null); // null = todo el año
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());
  const [sinPctOpen, setSinPctOpen] = useState(false);
  const [selectedFianza, setSelectedFianza] = useState(null);
  const { options, grupos } = useConvenioScope(convenioId);

  useEffect(() => {
    setPage(1);
    setExpanded(new Set());
  }, [groupBy, convenioId, selectedYear, mes]);

  const delAnio = useMemo(() => {
    const ids = new Set(grupos.map((g) => g.id));
    return movements.filter((m) => {
      if (!m.convenioId || !ids.has(m.convenioId)) return false;
      const ms = Number(m.fecha) || 0;
      return ms && new Date(ms).getFullYear() === selectedYear;
    });
  }, [movements, grupos, selectedYear]);

  // El mes acota la tabla y los totales; la gráfica siempre muestra el año
  // completo, si no el filtro esconde justo el contexto que lo justifica.
  const enScope = useMemo(
    () => (mes == null ? delAnio : delAnio.filter((m) => new Date(Number(m.fecha)).getMonth() === mes)),
    [delAnio, mes]
  );

  const nombreConvenio = useMemo(() => {
    const map = new Map();
    grupos.forEach((g) => map.set(g.id, g.nombre));
    return map;
  }, [grupos]);

  // El ledger guarda numeroFianza pero no la inclusión: se resuelve contra la
  // fianza viva, igual que en la pestaña de comisiones.
  const fianzaDocs = useMemo(() => {
    const map = new Map();
    Object.values(contractsById || {}).forEach((c) =>
      (c.fianzas || []).forEach((f) => {
        if (f.id) map.set(f.id, { fianza: f, contrato: c });
      })
    );
    return map;
  }, [contractsById]);

  const numeroConInclusion = (f) => {
    const doc = fianzaDocs.get(f.fianzaId)?.fianza;
    return formatNumeroFianza(
      {
        numeroFianza: f.numeroFianza,
        numeroInclusion: doc?.numeroInclusion || doc?.xmlFianza?.numeroInclusion,
      },
      'Sin número'
    );
  };

  const keyFor = (m) => {
    if (groupBy === 'afianzadora') {
      const nombre = m.afianzadora || 'Sin afianzadora';
      return { key: `af:${nombre.toLowerCase()}`, label: nombre };
    }
    if (groupBy === 'proveedor') {
      // The fiado of a convenio fianza IS the proveedor. Normalized so the
      // same supplier does not split across XML spellings.
      const nombre = m.fiadoNombre || 'Sin proveedor';
      return { key: `prov:${normalizeName(nombre) || nombre.toLowerCase()}`, label: nombre };
    }
    return {
      key: `conv:${m.convenioId}`,
      label: nombreConvenio.get(m.convenioId) || m.convenioNombre || 'Convenio sin nombre',
    };
  };

  const { rows, totals, sinPctList } = useMemo(() => {
    const map = new Map();
    const t = blankGroup(null, null);
    // El aviso de "sin % de comisión" sólo servía para saber que faltaba algo,
    // no para ir a arreglarlo: aquí se guarda de qué fianzas se trata.
    const sinPct = new Map();

    enScope.forEach((m) => {
      const { key, label } = keyFor(m);
      if (!map.has(key)) map.set(key, blankGroup(key, label));
      const g = map.get(key);

      const neto = Number(m.comisionMonto) || 0;
      const base = m.comisionBase != null ? Number(m.comisionBase) : neto;
      if (m.comisionMonto == null) {
        g.sinPct += 1;
        t.sinPct += 1;
        // Sin fianzaId el movimiento no se puede abrir, pero igual se lista.
        const kSin = m.fianzaId || `mov:${m.numeroFianza || 'sin-numero'}:${m.fecha}`;
        if (!sinPct.has(kSin)) sinPct.set(kSin, { ...blankFianza(m), sinPctCount: 0 });
        const sf = sinPct.get(kSin);
        sf.sinPctCount += 1;
        sf.prima += Number(m.prima) || 0;
        sf.fecha = Math.max(sf.fecha, Number(m.fecha) || 0);
        if (m.tipo) sf.tipos.add(m.tipo);
      }
      if (neto >= 0) {
        g.bruta += base;
        t.bruta += base;
      } else {
        g.cargos += neto;
        t.cargos += neto;
      }
      if (m.splitRol && m.comisionBase != null && m.comisionMonto != null) {
        const cedida = Number(m.comisionBase) - Number(m.comisionMonto);
        g.participacion += cedida;
        t.participacion += cedida;
      }
      g.neta += neto;
      t.neta += neto;

      switch (m.estadoCobro) {
        case 'cobrada':
          g.cobrada += neto;
          t.cobrada += neto;
          break;
        case 'pendiente':
          g.porCobrar += neto;
          t.porCobrar += neto;
          break;
        case 'adeudo':
          g.adeudo += Math.abs(neto);
          t.adeudo += Math.abs(neto);
          break;
        default:
          break;
      }
      g.movimientos += 1;
      t.movimientos += 1;

      // Detalle por fianza: una fianza puede traer emisión + endosos + cargos.
      if (m.fianzaId) {
        if (!g.fianzas.has(m.fianzaId)) g.fianzas.set(m.fianzaId, blankFianza(m));
        const f = g.fianzas.get(m.fianzaId);
        f.prima += Number(m.prima) || 0;
        f.bruta += neto >= 0 ? base : 0;
        f.neta += neto;
        f.movimientos += 1;
        f.fecha = Math.max(f.fecha, Number(m.fecha) || 0);
        if (m.tipo) f.tipos.add(m.tipo);
        if (m.estadoCobro) f.estados.add(m.estadoCobro);
      }
    });

    map.forEach((g) => {
      g.fianzasList = Array.from(g.fianzas.values()).sort((a, b) => b.neta - a.neta);
    });

    return {
      rows: Array.from(map.values()).sort((a, b) => b.neta - a.neta),
      totals: t,
      sinPctList: Array.from(sinPct.values()).sort((a, b) => b.fecha - a.fecha),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enScope, groupBy, nombreConvenio]);

  // Serie anual completa, con el mes seleccionado resaltado.
  const monthly = useMemo(() => {
    const porMes = MONTHS.map((etiqueta, i) => ({ mes: etiqueta, indice: i, neta: 0 }));
    delAnio.forEach((m) => {
      const i = new Date(Number(m.fecha)).getMonth();
      if (i >= 0 && i < 12) porMes[i].neta += Number(m.comisionMonto) || 0;
    });
    return porMes;
  }, [delAnio]);

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const openFianza = (f) => {
    const doc = fianzaDocs.get(f.fianzaId);
    if (!doc) return;
    setSelectedFianza({
      ...doc.fianza,
      contratoId: doc.fianza.contratoId || doc.contrato.id,
      _contrato: doc.contrato,
    });
  };

  // Plantilla de columnas del detalle expandido. Las de texto son minmax(0,…fr)
  // para que `truncate` tenga de dónde recortar: con min-width y sin tope, un
  // nombre largo de proveedor o afianzadora empuja el renglón y lo parte entero.
  const detalleCols = [
    'minmax(120px, 170px)',
    groupBy !== 'proveedor' ? 'minmax(0, 1.4fr)' : null,
    groupBy !== 'afianzadora' ? 'minmax(0, 1.4fr)' : null,
    '92px',
    'minmax(0, 0.7fr)',
    '116px',
    'minmax(90px, 0.6fr)',
    'minmax(90px, 0.6fr)',
  ]
    .filter(Boolean)
    .join(' ');

  const paged = rows.slice((page - 1) * perPage, page * perPage);
  const hayMovimiento = monthly.some((m) => m.neta !== 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <Heading level={2} className="text-xl text-azul-surelia">Comisión por convenio</Heading>
          <Text className="text-sm text-azul-surelia/70">
            Comisión de las fianzas de convenios de proveedores en{' '}
            {mes == null ? selectedYear : `${MONTHS_LARGOS[mes]} de ${selectedYear}`}
          </Text>
        </div>
        <ConvenioPicker value={convenioId} onChange={setConvenioId} options={options} />
      </div>

      {rows.length === 0 && mes == null ? (
        <EmptyScope>Sin movimientos de comisión de convenios en {selectedYear}.</EmptyScope>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <StatTile label="Comisión bruta" value={fmtMXN(totals.bruta)} sub="antes del split" />
            <StatTile
              label="Participación cedida"
              value={fmtMXN(totals.participacion)}
              sub="del otro agente"
            />
            <StatTile label="Comisión neta" value={fmtMXN(totals.neta)} sub={`${totals.movimientos} movimientos`} />
            <StatTile label="Cobrada" value={fmtMXN(totals.cobrada)} />
            <StatTile
              label="Por cobrar"
              value={fmtMXN(totals.porCobrar)}
              sub={totals.adeudo > 0 ? `${fmtMXN(totals.adeudo)} de adeudo` : null}
              alert={totals.adeudo > 0}
            />
          </div>

          {totals.sinPct > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50">
              <button
                type="button"
                onClick={() => setSinPctOpen((o) => !o)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left"
              >
                <ExclamationTriangleIcon className="w-4 h-4 shrink-0 text-amber-600" />
                <Text className="flex-1 text-sm text-amber-800">
                  {totals.sinPct} movimiento{totals.sinPct !== 1 ? 's' : ''} sin % de comisión: no suman al
                  neto. Define el % desde la fianza.
                </Text>
                <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-amber-800">
                  {sinPctOpen ? 'Ocultar' : `Ver ${sinPctList.length === 1 ? 'la fianza' : 'las fianzas'}`}
                  {sinPctOpen ? (
                    <ChevronDownIcon className="h-4 w-4" />
                  ) : (
                    <ChevronRightIcon className="h-4 w-4" />
                  )}
                </span>
              </button>

              {sinPctOpen && (
                <div className="space-y-1 border-t border-amber-200 px-3 py-2">
                  {sinPctList.map((f) => {
                    const abrible = fianzaDocs.has(f.fianzaId);
                    const tipos = Array.from(f.tipos)
                      .map((t) => TIPO_LABELS[t] || t)
                      .join(' + ');
                    return (
                      <button
                        key={f.fianzaId || `${f.numeroFianza}-${f.fecha}`}
                        type="button"
                        onClick={() => openFianza(f)}
                        disabled={!abrible}
                        title={abrible ? 'Abrir la fianza para definir el %' : 'El movimiento no tiene fianza asociada'}
                        className={`grid w-full grid-cols-[minmax(120px,170px)_minmax(0,1.4fr)_minmax(0,1.2fr)_92px_minmax(0,0.8fr)] items-center gap-x-4 rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-left transition-colors ${
                          abrible ? 'hover:border-amber-400' : 'cursor-default opacity-80'
                        }`}
                      >
                        <span className="truncate font-mono text-sm text-zinc-700">
                          {numeroConInclusion(f)}
                        </span>
                        <span className="truncate text-xs text-zinc-500" title={f.fiadoNombre || ''}>
                          {f.fiadoNombre || 'Sin proveedor'}
                        </span>
                        <span className="truncate text-xs text-zinc-500" title={f.afianzadora || ''}>
                          {f.afianzadora || 'Sin afianzadora'}
                        </span>
                        <span className="text-xs text-zinc-400">{fmtFecha(f.fecha)}</span>
                        <span className="truncate text-xs text-zinc-500" title={tipos}>
                          {tipos}
                          {f.sinPctCount > 1 && (
                            <span className="text-zinc-400"> ({f.sinPctCount} mov.)</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {hayMovimiento && (
            <div className="mb-5">
              <div className="flex items-center justify-between gap-3 mb-1">
                <Text className="text-[11px] uppercase tracking-wide text-gray-500">
                  Comisión neta por mes
                </Text>
                {mes != null && (
                  <button
                    type="button"
                    onClick={() => setMes(null)}
                    className="inline-flex items-center gap-1 rounded-full bg-azul-surelia/10 px-2 py-0.5 text-xs font-medium text-azul-surelia hover:bg-azul-surelia/20"
                  >
                    {MONTHS_LARGOS[mes]}
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={monthly}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => fmtMXN(v)} width={90} />
                    <Tooltip formatter={(v) => fmtMXN(v)} labelFormatter={(l) => `Mes: ${l}`} />
                    <Bar
                      dataKey="neta"
                      name="Comisión neta"
                      cursor="pointer"
                      onClick={(_entry, indice) => setMes((actual) => (actual === indice ? null : indice))}
                    >
                      {monthly.map((d) => (
                        <Cell
                          key={d.indice}
                          fill="#284865"
                          fillOpacity={mes == null || mes === d.indice ? 1 : 0.25}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Text className="text-[11px] text-gray-400 mt-1">
                Haz clic en un mes para acotar la tabla.
              </Text>
            </div>
          )}

          <div className="flex items-center gap-2 mb-3">
            <Text className="text-xs uppercase tracking-wide text-gray-500">Agrupar por</Text>
            <div className="flex rounded-lg border border-azul-surelia/20 overflow-hidden">
              {GROUPS.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setGroupBy(g.id)}
                  className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                    groupBy === g.id
                      ? 'bg-azul-surelia text-white'
                      : 'bg-white text-azul-surelia hover:bg-azul-surelia/5'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>

          {rows.length === 0 ? (
            <EmptyScope>Sin movimientos en {MONTHS_LARGOS[mes]} de {selectedYear}.</EmptyScope>
          ) : (
            <>
              <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
                <Table>
                  <TableHead className="bg-azul-surelia text-white">
                    <TableRow>
                      <TableHeader className="text-white w-8"></TableHeader>
                      <TableHeader className="text-white">{GROUPS.find((g) => g.id === groupBy)?.label}</TableHeader>
                      <TableHeader className="text-white text-right">Fianzas</TableHeader>
                      <TableHeader className="text-white text-right">Bruta</TableHeader>
                      <TableHeader className="text-white text-right">Participación</TableHeader>
                      <TableHeader className="text-white text-right">Cargos</TableHeader>
                      <TableHeader className="text-white text-right">Neta</TableHeader>
                      <TableHeader className="text-white text-right">Cobrada</TableHeader>
                      <TableHeader className="text-white text-right">Por cobrar</TableHeader>
                      <TableHeader className="text-white text-right">Adeudo</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((r) => {
                      const isOpen = expanded.has(r.key);
                      return (
                        <React.Fragment key={r.key}>
                          <TableRow className="cursor-pointer hover:bg-azul-surelia/5" onClick={() => toggle(r.key)}>
                            <TableCell className="py-3 pr-0">
                              {isOpen ? (
                                <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                              ) : (
                                <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
                              )}
                            </TableCell>
                            <TableCell className="py-3">
                              <Text className="text-sm font-medium text-azul-surelia truncate max-w-[260px]" title={r.label}>
                                {r.label}
                              </Text>
                              {r.sinPct > 0 && (
                                <Text className="text-[11px] text-amber-700">{r.sinPct} sin %</Text>
                              )}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                              {r.fianzasList.length}
                              {r.movimientos !== r.fianzasList.length && (
                                <span className="text-zinc-400" title="Movimientos del ledger (emisión, endosos, cargos)">
                                  {' '}
                                  ({r.movimientos} mov.)
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                              {fmtMXN(r.bruta)}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-500">
                              {r.participacion > 0 ? `- ${fmtMXN(r.participacion)}` : '-'}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-500">
                              {r.cargos < 0 ? fmtMXN(r.cargos) : '-'}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm font-semibold text-azul-surelia">
                              {fmtMXN(r.neta)}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-green-700">
                              {fmtMXN(r.cobrada)}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                              {fmtMXN(r.porCobrar)}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm">
                              {r.adeudo > 0 ? (
                                <span className="font-semibold text-red-600">{fmtMXN(r.adeudo)}</span>
                              ) : (
                                <span className="text-zinc-400">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow className="bg-azul-surelia/5">
                              <TableCell colSpan={10} className="py-2 px-6">
                                {r.fianzasList.length === 0 ? (
                                  <Text className="text-sm text-zinc-500">
                                    Movimientos sin fianza asociada.
                                  </Text>
                                ) : (
                                  <div className="space-y-1">
                                    <div
                                      className="grid items-center gap-x-4 px-3 pb-0.5 text-[11px] uppercase tracking-wide text-zinc-400"
                                      style={{ gridTemplateColumns: detalleCols }}
                                    >
                                      <span>Fianza</span>
                                      {groupBy !== 'proveedor' && <span>Proveedor</span>}
                                      {groupBy !== 'afianzadora' && <span>Afianzadora</span>}
                                      <span>Fecha</span>
                                      <span>Tipo</span>
                                      <span>Estado</span>
                                      <span className="text-right">Prima</span>
                                      <span className="text-right">Neta</span>
                                    </div>
                                    {r.fianzasList.map((f) => {
                                      const abrible = fianzaDocs.has(f.fianzaId);
                                      const estado = f.estados.has('adeudo')
                                        ? 'adeudo'
                                        : f.estados.has('pendiente')
                                        ? 'pendiente'
                                        : f.estados.has('cobrada')
                                        ? 'cobrada'
                                        : Array.from(f.estados)[0];
                                      const badge = ESTADO_COBRO_LABELS[estado];
                                      const tipos = Array.from(f.tipos)
                                        .map((t) => TIPO_LABELS[t] || t)
                                        .join(' + ');
                                      return (
                                        <button
                                          key={f.fianzaId}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openFianza(f);
                                          }}
                                          disabled={!abrible}
                                          style={{ gridTemplateColumns: detalleCols }}
                                          className={`grid w-full items-center gap-x-4 text-left bg-white rounded-lg border border-zinc-200 px-3 py-1.5 transition-colors ${
                                            abrible ? 'hover:border-azul-surelia/40' : 'cursor-default opacity-80'
                                          }`}
                                        >
                                          <span className="text-sm font-mono text-zinc-700 truncate">
                                            {numeroConInclusion(f)}
                                          </span>
                                          {groupBy !== 'proveedor' && (
                                            <span className="text-xs text-zinc-500 truncate" title={f.fiadoNombre || ''}>
                                              {f.fiadoNombre || 'Sin proveedor'}
                                            </span>
                                          )}
                                          {groupBy !== 'afianzadora' && (
                                            <span className="text-xs text-zinc-500 truncate" title={f.afianzadora || ''}>
                                              {f.afianzadora || 'Sin afianzadora'}
                                            </span>
                                          )}
                                          <span className="text-xs text-zinc-400">{fmtFecha(f.fecha)}</span>
                                          <span className="text-xs text-zinc-500 truncate" title={tipos}>
                                            {tipos}
                                          </span>
                                          <span className="min-w-0">
                                            {badge && (
                                              <span
                                                className={`inline-block max-w-full truncate px-2 py-0.5 text-xs font-semibold rounded-full ${badge.className}`}
                                              >
                                                {badge.label}
                                              </span>
                                            )}
                                          </span>
                                          <span className="text-xs text-zinc-500 tabular-nums text-right" title="Prima">
                                            {fmtMXN(f.prima)}
                                          </span>
                                          <span
                                            className="text-xs font-semibold text-azul-surelia tabular-nums text-right"
                                            title="Comisión neta"
                                          >
                                            {fmtMXN(f.neta)}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <TablePager
                total={rows.length}
                page={page}
                onPage={setPage}
                perPage={perPage}
                itemLabel={
                  groupBy === 'convenio' ? 'convenios' : groupBy === 'afianzadora' ? 'afianzadoras' : 'proveedores'
                }
              />
            </>
          )}
        </>
      )}

      {selectedFianza &&
        createPortal(
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={{}}
            contratoData={selectedFianza._contrato}
            onUpdate={() => {}}
          />,
          document.body
        )}
    </div>
  );
}
